import { RefObject, useEffect, useState } from 'react';
import { ObjectType } from 'typescript';

import { CheckBox, CheckBoxDomRef, Ui5CustomEvent, ValueState } from '@ui5/webcomponents-react';
import { QtProjectImport } from 'generated/graphql';

type SupportedDataTypes = QtProjectImport | ObjectType;

interface UseTableCheckBoxesProps {
  data: Array<SupportedDataTypes> | undefined;
  onSelected: (selection: Array<any>) => void;
  tableInstance: RefObject<Record<string, any>>;
  shouldCheckHeaderCheckbox?: boolean;
}

interface RenderCheckBoxProps {
  dataTestId?: string;
  dataHelpId?: string;
  valueState?: ValueState;
}

const getSelectedElementsAsDTO = (
  data: Array<SupportedDataTypes> | undefined,
  selectedIndexes: number[]
): Array<SupportedDataTypes> => {
  if (!data || selectedIndexes.length == 0) {
    return [];
  }

  return selectedIndexes.map((selectedIndex) => {
    return data[selectedIndex];
  });
};

export const useTableCheckBoxes = ({
  data,
  onSelected,
  tableInstance,
  shouldCheckHeaderCheckbox = true
}: UseTableCheckBoxesProps) => {
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const indexList = data ? Array.from(data, (_: SupportedDataTypes, index: number) => index) : [];

  useEffect(() => {
    setSelectedIndexes(shouldCheckHeaderCheckbox ? indexList : []);
  }, [indexList.length]);

  useEffect(() => {
    onSelected(getSelectedElementsAsDTO(data, selectedIndexes));
  }, [selectedIndexes]);

  const handleFiltersApplied = (
    currentTableInstance: Record<string, any>,
    checked: boolean | undefined
  ) => {
    const filteredIndexes = currentTableInstance.filteredRows.map(
      (filteredRow: Record<string, any>) => filteredRow.index
    );
    const isAlreadySelected = filteredIndexes.every((index: number) =>
      selectedIndexes.includes(index)
    );

    if (checked && !isAlreadySelected) {
      setSelectedIndexes((prev) => [...prev, ...filteredIndexes]);
    } else {
      setSelectedIndexes(selectedIndexes.filter((index) => !filteredIndexes.includes(index)));
    }
  };

  const onHeaderCheckBoxChange = (event: Ui5CustomEvent<CheckBoxDomRef>) => {
    const { checked } = event.target;

    const currentTableInstance = tableInstance.current!;
    const hasNoFiltersApplied = currentTableInstance.state.filters.length === 0;

    if (hasNoFiltersApplied) {
      setSelectedIndexes(checked ? indexList : []);
      return;
    }

    handleFiltersApplied(currentTableInstance, checked);
  };

  const onCheckBoxChange = (index: any, event: Ui5CustomEvent<CheckBoxDomRef>) => {
    const { checked } = event.target;

    if (checked) {
      setSelectedIndexes((prev) => [...prev, index]);
    } else {
      setSelectedIndexes((prev) => prev.filter((id) => id !== index));
    }
  };

  const renderHeaderCheckbox = ({ dataHelpId, dataTestId }: RenderCheckBoxProps) => {
    const currentTableInstance = tableInstance.current!;
    const isIndeterminate = currentTableInstance.filteredRows
      .map((filteredRow: Record<string, any>) => filteredRow.index)
      .some((index: number) => !selectedIndexes.includes(index));

    return (
      <CheckBox
        onChange={onHeaderCheckBoxChange}
        checked={selectedIndexes.length > 0}
        indeterminate={isIndeterminate}
        data-testid={dataTestId}
        data-help-id={dataHelpId}
      />
    );
  };

  const renderCheckbox = (
    index: any,
    { dataHelpId, dataTestId, valueState }: RenderCheckBoxProps
  ) => {
    return (
      <CheckBox
        onChange={(event) => onCheckBoxChange(index, event)}
        checked={selectedIndexes.includes(index)}
        valueState={valueState}
        data-testid={dataTestId}
        data-help-id={dataHelpId}
        role="checkbox"
      />
    );
  };
  return { renderHeaderCheckbox, renderCheckbox, indexes: selectedIndexes };
};
